import * as React from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { GridToolbarContainer, GridActionsCellItem } from "@mui/x-data-grid";

import { default as DataGrid } from "../../../components/LocalizedDataGrid";

import { useTranslation } from "react-i18next";

import PointEditPopUp from "./PointEditPopUp";
import { ApiContext } from "../../../App";

const BINARY = "BINARY";
const HVAC = "HVAC";
const INPUT = "INPUT";

const defaultOutputData = {
	category: HVAC,
	identifier: "",
	on_off_identifier: "",
	mode_identifier: "",
	fan_speed_identifier: "",
	location: "",
	system: "",
	name: "",
	tariff: "",
	controller: "",
	floor_plan: "",
	power_rating: 0.0,
	cycle_duration: null,
	set_points: "",
	sensitivity_heating: 0.4,
	sensitivity_cooling: 0.4,
	sensitivity_off: 0.1,
	azimuth: 0,
	inclination: 0,
	solar_gain_factor: 0.0,
	max_temp_change_heating: 5,
	max_temp_change_cooling: 5,
	input_temp_heating: 30,
	input_temp_cooling: 16,
	dead_band: 0.25,
	pre_heat: "2.0",
	has_heating: true,
	has_cooling: true,
	managed: true,
	must_be_set_to_constraints: [],
	temperature_constraints: [],
	key: null,
	display_on_floorplan: false,
};

const defaultInputData = {
	category: INPUT,
	identifier: "",
	location: "",
	on_off_identifier: null,
	mode_identifier: null,
	fan_speed_identifier: null,
	system: "",
	name: "",
	tariff: null,
	controller: "",
	floor_plan: "",
	power_rating: null,
	cycle_duration: null,
	set_points: null,
	sensitivity_off: null,
	azimuth: null,
	inclination: null,
	solar_gain_factor: null,
	max_temp_change_heating: null,
	max_temp_change_cooling: null,
	input_temp_heating: null,
	input_temp_cooling: null,
	dead_band: null,
	pre_heat: null,
	has_heating: null,
	has_cooling: null,
	managed: null,
	must_be_set_to_constraints: null,
	temperature_constraints: null,
	key: "TEMPERATURE",
	display_on_floorplan: false,
};

function EditToolbar(props) {
	// the onClick function returns the default data that corresponds to the user's selected option (input/output)

	const { onClick, facility } = props;
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSelectOutput = () => {
		setAnchorEl(null);
		onClick({ ...defaultOutputData, facility: facility });
	};

	const handleSelectInput = () => {
		setAnchorEl(null);
		onClick({ ...defaultInputData, facility: facility });
	};

	return (
		<GridToolbarContainer>
			<Button
				color="primary"
				startIcon={<AddIcon />}
				onClick={handleClick}
			>
				{t("Add point")}
			</Button>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
			>
				<MenuItem onClick={handleSelectInput}>{t("Input")}</MenuItem>
				<MenuItem onClick={handleSelectOutput}>{t("Output")}</MenuItem>
			</Menu>
		</GridToolbarContainer>
	);
}

export default function PointEditor(props) {
	const {  onChange, points, tariffs, controllers, floorPlans, facility } =
		props;

	const { t } = useTranslation();

	const [rows, setRows] = React.useState([]);
	const [alertOpen, setAlertOpen] = React.useState(false);
	const [alertMessage, setAlertMessage] = React.useState("");
	const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
	const [deleteCandidate, setDeleteCandidate] = React.useState();
	const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
	const [editPopUpData, setEditPopUpData] = React.useState({
		...defaultOutputData,
		facility: facility,
	});

	const {pointsApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		let data_with_id = points.map((dict) => {
			let with_id_url = { ...dict, id: dict.url };
			delete with_id_url.url;
			return with_id_url;
		});
		setRows(data_with_id);
	}, [points]);

	const handleEditClick = (id) => {
		setEditPopUpOpen(true);
		setEditPopUpData(rows.find((item) => item.id === id));
	};

	const handleAddClick = (defaultData) => {
		setEditPopUpData(defaultData);
		setEditPopUpOpen(true);
	};

	const handleEditPopUpClosedWithoutSaving = () => {
		setEditPopUpOpen(false);
	};

	const handleEditPopUpClosedWithUpdate = (new_data) => {
		if (rows.find((item) => item.id === new_data.id))
			setRows(rows.map((row) => (row.id === new_data.id ? new_data : row)));
		else setRows((oldRows) => [...oldRows, new_data]);

		setEditPopUpOpen(false);

		//notify change to parent
		if (onChange) {
			onChange();
		}
	};

	const handleDeleteClick = (id) => {
		setDeleteCandidate(id);
		setDeleteConfirmOpen(true);
	};

	const handleDelete = (id) => {
		pointsApi.Delete(
			id,
			() => {
				setRows(rows.filter((row) => row.id !== id));
				//notify change to parent
				if (onChange) {
					onChange();
				}
			},
			() => {
				setAlertMessage(t("The point couldn't be deleted."));
				setAlertOpen(true);
			}
		);

		setDeleteCandidate(null);
	};

	const handleDuplicateClick = (id) => {
		const from_data = rows.filter((row) => row.id === id)[0];
		let to_data = { ...from_data };
		delete to_data.id;

		setEditPopUpData(to_data);
		setEditPopUpOpen(true);
	};

	const getControllerName = (params) => {
		const controller = controllers.filter(
			(item) => item.url === params.row.controller
		);
		if (controller.length > 0) return controller[0].name;
		return "";
	};

	const columns = [
		{
			field: "category",
			headerName: t("Category"),
			flex: 1,
			minWidth: 80,
			valueFormatter: (val) => (val.value === INPUT ? t("Input") : t("Output")),
		},
		{
			field: "controller_name",
			headerName: t("Controller"),
			flex: 1,
			minWidth: 80,
			valueGetter: getControllerName,
		},
		{ field: "identifier", headerName: t("ID"), flex: 1, minWidth: 200 },
		{ field: "location", headerName: t("Location"), flex: 1, minWidth: 200 },
		{ field: "system", headerName: t("System"), flex: 1, minWidth: 180 },
		{ field: "name", headerName: t("Device"), flex: 1, minWidth: 180 },
		{
			field: "managed",
			headerName: t("Automated"),
			type: "boolean",
			flex: 0.5,
			minWidth: 100,
		},
		{
			field: "actions",
			type: "actions",
			headerName: t("Actions"),
			flex: 0.5,
			minWidth: 150,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<EditIcon />}
						label={t("Edit")}
						className="textPrimary"
						onClick={() => handleEditClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<ContentCopyIcon />}
						label={t("Duplicate")}
						onClick={() => handleDuplicateClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<DeleteIcon />}
						label={t("Delete")}
						onClick={() => handleDeleteClick(id)}
						color="inherit"
					/>,
				];
			},
		},
	];

	return (
		<>
			<PointEditPopUp
				open={editPopUpOpen}
				data={editPopUpData}
				onCloseWithoutSaving={handleEditPopUpClosedWithoutSaving}
				onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
				tariffs={tariffs}
				controllers={controllers}
				floorPlans={floorPlans}
			/>
			<Dialog
				open={alertOpen}
				onClose={() => setAlertOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Problem")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{alertMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setAlertOpen(false)}
						autoFocus
					>
						{t("OK")}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
			>
				<DialogTitle id="alert-dialog-title">{t("Confirmation")}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{t("Are you sure you want to delete this point?")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDeleteConfirmOpen(false);
							handleDelete(deleteCandidate);
						}}
					>
						{t("YES")}
					</Button>
					<Button
						onClick={() => setDeleteConfirmOpen(false)}
						autoFocus
					>
						{t("NO")}
					</Button>
				</DialogActions>
			</Dialog>
			<DataGrid
				rows={rows}
				columns={columns}
				components={{
					Toolbar: EditToolbar,
				}}
				componentsProps={{
					toolbar: { onClick: handleAddClick, facility: facility },
				}}
				experimentalFeatures={{ newEditingApi: true }}
			/>
		</>
	);
}
